import { createServerFn } from '@tanstack/react-start';
import { getCookie, getRequestHeader, getRequestIP, getRequestUrl } from '@tanstack/react-start/server';
import { postContactForm } from '@worldcoo/monolith/apis/portals';
import { portalLocales } from '@worldcoo/monolith/portals';
import { categories, categoriesKeys, detectLocale, WORLDCOO_MERCHANT_ID } from '@worldcoo/monolith/core';
import { getLegalContentHtml, type LegalContentType } from '@/content/legal-content';
import {
	sketchDonationInputSchema,
	paymentMethodsInputSchema,
	paymentDetailsInputSchema,
	processPaymentInputSchema,
} from '@worldcoo/monolith/payments/shared';
import {
	sketchDonation,
	getAdyenPaymentMethods,
	getAdyenPaymentDetails,
	initPayment,
} from '@worldcoo/monolith/payments';
import * as z from 'zod';
import { Resource } from 'sst';
import { eventEmitter } from './server-broadcast';
import { Searcher } from '@worldcoo/shared/searcher';
import { portalLocalesExtended, contactFormSchema } from '@worldcoo/monolith/portals';
import cityToCountry from './city-iata-to-country.json';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { cache, refetch } from './server-cache';
import {
	portalAuthorCacheOptions,
	portalMetadataCacheOptions,
	portalCampaignsCacheOptions,
	portalCampaignCacheOptions,
	portalCampaignMetadataCacheOptions,
	portalCampaignsAggregatesCacheOptions,
	worldcooGlobalStatsCacheOptions,
	recentDonationsCacheOptions,
} from './server-cache-options';

const requestContextSchema = z.object({ portalId: z.string(), locale: z.enum(portalLocales) });

export const getRequestContext = createServerFn()
	.inputValidator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

export const getPortalAuthorFn = createServerFn()
	.inputValidator(requestContextSchema)
	.handler(({ data: { portalId } }) => {
		return cache(portalAuthorCacheOptions(portalId));
	});

export const getPortalMetadataFn = createServerFn()
	.inputValidator(requestContextSchema)
	.handler(async ({ data: { portalId, locale } }) => {
		const metadata = await cache(portalMetadataCacheOptions(portalId, locale));
		if ('notFound' in metadata) return metadata;
		return {
			...metadata,
			texts: {
				...metadata.texts,
				termsAndConditions: undefined,
				privacyNotice: undefined,
			},
		};
	});

export const getPortalCampaignMetadataFn = createServerFn()
	.inputValidator(z.object({ ...requestContextSchema.shape, campaignId: z.string() }))
	.handler(({ data: { portalId, locale, campaignId } }) => {
		return cache(portalCampaignMetadataCacheOptions(portalId, locale, campaignId));
	});

/**
 * Get aggregates for all campaigns in a portal with timeout handling
 * Returns undefined if the request times out (with console error)
 * This allows the UI to fallback gracefully when aggregates can't be loaded
 */
export const getPortalCampaignsAggregatesFn = createServerFn()
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(({ data: { portalId } }) => {
		return cache(portalCampaignsAggregatesCacheOptions(portalId));
	});

export const getPortalCampaignsFn = createServerFn()
	.inputValidator(
		z.object({
			...requestContextSchema.shape,
			query: z.string().optional(),
			year: z.number().optional(),
			state: z.literal('finished').optional(),
			causes: z.array(z.enum(categoriesKeys)).optional(),
			city: z.string().optional(),
		}),
	)
	.handler(async ({ data: { portalId, locale, query, year, state, causes, city } }) => {
		const result = await cache(portalCampaignsCacheOptions(portalId, locale));
		if ('notFound' in result) return result;

		const countryCode = city ? cityToCountry[city as keyof typeof cityToCountry] : undefined;
		const normalizedQuery = query ? Searcher.normalizeString(query) : undefined;

		// Single pass through campaigns to collect all needed data
		const yearSet = new Set<number>();
		let activeCampaignsCount = 0;
		const filteredCampaigns: Array<{ campaign: (typeof result.campaigns)[0]; matchesCountry: boolean }> = [];

		for (const campaign of result.campaigns) {
			// Count active campaigns
			if (!campaign.finished) {
				activeCampaignsCount++;
			}

			// Collect years
			if (campaign.startDate) {
				const campaignYear = new Date(campaign.startDate).getFullYear();
				yearSet.add(campaignYear);
			}

			// Apply filters
			// Year filter
			if (year && campaign.startDate && new Date(campaign.startDate).getFullYear() !== year) {
				continue;
			}

			// Causes filter
			if (causes && causes.length > 0) {
				const hasCause = causes.some((cause) => campaign.categories.includes(categories[cause]));
				if (!hasCause) continue;
			}

			// Query filter
			if (normalizedQuery) {
				const normalizedValue = Searcher.normalizeString(
					`${campaign.id} ${campaign.name} ${campaign.ngos.map((ngo) => ngo.name).join(' ')}`,
				);
				if (!normalizedValue.includes(normalizedQuery)) {
					continue;
				}
			}

			// Track whether campaign matches country filter
			const matchesCountry = !countryCode || campaign.countryCode === countryCode;
			filteredCampaigns.push({ campaign, matchesCountry });
		}

		// Determine final campaigns based on country filter logic
		const hasCountryMatches = filteredCampaigns.some((item) => item.matchesCountry && !item.campaign.finished);
		const finalCampaigns: typeof result.campaigns = [];
		let filteredActiveCampaignsCount = 0;

		for (const { campaign, matchesCountry } of filteredCampaigns) {
			// If we have country code and no active matches, show all filtered campaigns
			// Otherwise, only show campaigns that match the country filter
			if (countryCode && !hasCountryMatches) {
				// Show all filtered campaigns (ignore country filter)
				finalCampaigns.push(campaign);
				if (!campaign.finished) filteredActiveCampaignsCount++;
			} else if (matchesCountry) {
				// Only show campaigns matching country
				finalCampaigns.push(campaign);
				if (!campaign.finished) filteredActiveCampaignsCount++;
			}
		}

		// Apply state filter to final campaigns
		const campaigns: typeof result.campaigns = [];
		for (const campaign of finalCampaigns) {
			// Show finished campaigns if there are no active campaigns
			if (activeCampaignsCount === 0) {
				campaigns.push(campaign);
				continue;
			}

			if (!state && campaign.finished) continue;
			if (state === 'finished' && !campaign.finished) continue;

			campaigns.push(campaign);
		}

		// There is not a wide support for toSorted() in the browser
		// oxlint-disable-next-line unicorn/no-array-sort
		const yearOptions = Array.from(yearSet).sort((a, b) => b - a);

		return {
			campaignsCount: result.campaigns.length,
			activeCampaignsCount,
			filteredCampaignsCount: finalCampaigns.length,
			filteredActiveCampaignsCount,
			campaigns,
			yearOptions,
		};
	});

export const getPortalCampaignFn = createServerFn()
	.inputValidator(z.object({ ...requestContextSchema.shape, campaignId: z.string() }))
	.handler(({ data: { portalId, locale, campaignId } }) => {
		return cache(portalCampaignCacheOptions(portalId, locale, campaignId));
	});

export const preregisterDonationFn = createServerFn({ method: 'POST' })
	.inputValidator(sketchDonationInputSchema)
	.handler(async ({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');
		const request = parseRequest();

		const applicationId = data.applicationId;
		if (applicationId.type !== applicationType.portal) {
			throw new Error('Only portal application type is supported');
		}
		const portalAuthor = await cache(portalAuthorCacheOptions(applicationId.host));
		if ('notFound' in portalAuthor) throw new Error('Portal not found');

		const merchantId = portalAuthor.type === 'merchant' ? portalAuthor.merchantId : WORLDCOO_MERCHANT_ID;

		return sketchDonation({
			sourceIp: requestIp,
			origin: request.baseUrl,
			language: request.locale,
			userAgent: getRequestHeader('User-Agent') ?? undefined,
			merchantId,
			input: data,
		});
	});

export const getPaymentsMethodsFn = createServerFn({ method: 'POST' })
	.inputValidator(paymentMethodsInputSchema)
	.handler(({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return getAdyenPaymentMethods({ sourceIp: requestIp, input: data });
	});

export const getPaymentDetailsFn = createServerFn({ method: 'POST' })
	.inputValidator(paymentDetailsInputSchema)
	.handler(({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return getAdyenPaymentDetails({ sourceIp: requestIp, input: data });
	});

export const processPaymentFn = createServerFn({ method: 'POST' })
	.inputValidator(processPaymentInputSchema)
	.handler(({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return initPayment({
			sourceIp: requestIp,
			origin: request.baseUrl,
			input: data,
		});
	});

export const waitForConfirmedDonationFn = createServerFn()
	.inputValidator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};

			eventEmitter.on('donation.confirmed', onConfirmed);

			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});

		yield await promise;
	});

export const refreshPortalAggregatesCacheFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(async ({ data: { portalId } }) => {
		// Only refresh aggregates - campaigns and campaign data no longer contain aggregates
		await refetch(portalCampaignsAggregatesCacheOptions(portalId));
	});

export const postContactFormFn = createServerFn({ method: 'POST' })
	.inputValidator(contactFormSchema)
	.handler(async ({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return postContactForm(data, {
			ip: requestIp,
			language: data.language,
			origin: request.baseUrl,
			userAgent: getRequestHeader('User-Agent') ?? '',
		});
	});

export const getWorldcooGlobalStatsFn = createServerFn().handler(() => {
	return cache(worldcooGlobalStatsCacheOptions());
});

export const getRecentDonationsFn = createServerFn()
	.inputValidator(
		z.object({
			windowKey: z.string(),
			mode: z.enum(['day', 'realtime']).default('day'),
		}),
	)
	.handler(({ data }) => {
		return cache(recentDonationsCacheOptions(data.windowKey, data.mode));
	});

export const logErrorFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			route: z.string().catch('unknown'),
			name: z.string().catch('UnknownError'),
			message: z.string().catch('(no message)'),
			stack: z.string().optional(),
		}),
	)
	.handler(({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();

		const logPayload = {
			type: 'PORTAL_FRONTEND_ERROR',
			timestamp: new Date().toISOString(),
			portalId: request.portalId,
			locale: request.locale,
			baseUrl: request.baseUrl,
			ip: requestIp,
			userAgent: getRequestHeader('User-Agent') ?? undefined,
			route: data.route,
			name: data.name,
			message: data.message,
			stack: data.stack,
		};

		console.error(`PORTAL_FRONTEND_ERROR: ${data.name} — ${data.message}`);
		console.info(logPayload);

		return { success: true, logged: true };
	});

const localeSchema = z.enum(portalLocalesExtended).or(z.undefined()).catch(undefined);

function parseRequest(clientLangSearchParam?: string) {
	const host = getRequestHeader('X-Forwarded-Host') ?? getRequestHeader('Host');
	if (!host) throw new Error('Host header is missing');

	const portalId = host.split('.')[0];
	if (!portalId) throw new Error('Invalid host');

	const langParam = localeSchema.parse(clientLangSearchParam ?? getRequestUrl().searchParams.get('lang'));
	const languageCookie = getCookie('language');
	const acceptLanguage = getRequestHeader('Accept-Language');
	const locale = detectLocale(langParam ?? languageCookie ?? acceptLanguage ?? 'es', portalLocales);

	const protocol = host.includes('localhost') ? 'http://' : 'https://';

	return {
		baseUrl: protocol + host,
		portalId,
		locale,
		isInIframe:
			(getRequestHeader('Sec-Fetch-Site') === 'cross-site' && getRequestHeader('Sec-Fetch-Dest') === 'iframe') ||
			getRequestUrl().searchParams.get('iframe') === 'true',
	};
}

export const getLegalContentFn = createServerFn()
	.inputValidator(z.object({ type: z.string(), locale: z.string() }))
	.handler(({ data: { type, locale } }) => {
		return getLegalContentHtml(type as LegalContentType, locale);
	});

function parseIp() {
	return (
		getRequestIP() ?? getRequestHeader('X-Forwarded-For') ?? (Resource.App.stage === 'dev' ? '127.0.0.1' : undefined)
	);
}
